/**
 * SitePostureBanner — Iter275.5
 *
 * Site-wide sticky top banner that surfaces the current site posture:
 *   • DEMO MODE — yellow, bold, all-caps. Signals to prospects + beta
 *     testers that nothing is real (no charges, synthetic data).
 *   • UNDER ATTACK — red, urgent. Signals to visitors that we're in
 *     a defensive posture (mutating endpoints rejected for anonymous
 *     users, aggressive rate limits applied).
 *
 * Mounted globally above the React Router outlet. Polls /api/site-flags
 * every 30s so a toggle from any admin propagates without a hard reload.
 *
 * Z-index is 9999 so the banner sits above Header, MegaMenu, modal
 * overlays — operators MUST always see the current posture.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertOctagon, Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 30_000;

export default function SitePostureBanner() {
  const [flags, setFlags] = useState({
    demo_mode: false, under_attack: false, under_attack_reason: null,
  });
  // Operators can dismiss the banner for the current session. The dismiss
  // is per-flag + per-tab so a fresh tab re-shows the warning.
  const [dismissed, setDismissed] = useState({ demo: false, attack: false });

  useEffect(() => {
    let cancelled = false;
    const fetchFlags = async () => {
      try {
        const r = await axios.get(`${API}/site-flags`, { timeout: 4000 });
        if (cancelled) return;
        setFlags(r.data || {});
      } catch (_e) { /* fail silent */ }
    };
    fetchFlags();
    const t = setInterval(fetchFlags, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const showAttack = flags.under_attack && !dismissed.attack;
  const showDemo   = flags.demo_mode    && !dismissed.demo;

  if (!showAttack && !showDemo) return null;

  return (
    <div data-testid="site-posture-stack"
         style={{
           position: "sticky", top: 0, zIndex: 9999,
           display: "flex", flexDirection: "column",
         }}>
      {showAttack && (
        <UnderAttackBanner reason={flags.under_attack_reason}
                           onDismiss={() => setDismissed((d) => ({ ...d, attack: true }))} />
      )}
      {showDemo && (
        <DemoModeBanner onDismiss={() => setDismissed((d) => ({ ...d, demo: true }))} />
      )}
    </div>
  );
}


function DemoModeBanner({ onDismiss }) {
  return (
    <div
      data-testid="banner-demo-mode"
      role="status"
      style={{
        background: "#FFB000",
        color: "#1A0F00",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        fontWeight: 800,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        fontSize: 13,
        fontFamily: "var(--qt-font-mono, monospace)",
        boxShadow: "0 2px 8px rgba(255,176,0,0.35)",
        position: "relative",
      }}
    >
      <Sparkles size={16} style={{ flexShrink: 0 }} />
      <span data-testid="banner-demo-mode-text">
        DEMO MODE · SYNTHETIC DATA · NO LIVE CHARGES · PROSPECTS &amp; BETA TESTERS ONLY
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss demo mode banner for this session"
        data-testid="banner-demo-mode-dismiss"
        style={{
          background: "transparent",
          border: 0,
          color: "#1A0F00",
          cursor: "pointer",
          padding: 4,
          opacity: 0.7,
          position: "absolute",
          right: 8,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}


function UnderAttackBanner({ reason, onDismiss }) {
  return (
    <div
      data-testid="banner-under-attack"
      role="alert"
      style={{
        background: "#7A1414",
        color: "#FFFFFF",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        fontWeight: 700,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        fontSize: 13,
        fontFamily: "var(--qt-font-mono, monospace)",
        borderBottom: "1px solid #FF6E6E",
        boxShadow: "0 2px 12px rgba(255,110,110,0.4)",
        position: "relative",
      }}
    >
      <span style={{
        display: "inline-block", width: 8, height: 8, borderRadius: 999,
        background: "#FF6E6E",
        boxShadow: "0 0 12px rgba(255,110,110,0.95)",
        animation: "qt-pulse 1.2s ease-in-out infinite",
      }} />
      <AlertOctagon size={16} style={{ flexShrink: 0 }} />
      <span data-testid="banner-under-attack-text">
        ELEVATED THREAT POSTURE · MUTATING OPERATIONS TEMPORARILY RESTRICTED
        {reason ? <> · {String(reason).slice(0, 120)}</> : null}
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss under-attack banner for this session"
        data-testid="banner-under-attack-dismiss"
        style={{
          background: "transparent",
          border: 0,
          color: "#FFFFFF",
          cursor: "pointer",
          padding: 4,
          opacity: 0.7,
          position: "absolute",
          right: 8,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
